import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useCallback } from 'react';
import { IoClose } from 'react-icons/io5';
import { useCreateTaskAutomation } from '../../../services/automation.queries';
import type { ICreateTaskAutomationRequest, IInlineChecklistItem } from '../../../types/tasks.types';
import { useToast } from '../../../../../context/ToastContext';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import type { IStep1FormValues } from './StepOne';
import type { IStep2FormValues } from './StepTwo';
import type { IStep3FormValues } from './StepThree';
import type { IStep4FormValues } from './StepFour';

interface ICreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const TOTAL_STEPS = 4;

// Step header config: title + description per step
const STEP_HEADERS: Record<number, { title: string; description: string }> = {
  1: { title: 'Automation.createNewTask', description: 'Automation.createTaskDescription' },
  2: { title: 'Automation.taskList', description: 'Automation.taskListDescription' },
  3: { title: 'Automation.selectProperties', description: 'Automation.selectPropertiesDescription' },
  4: { title: 'Automation.assignTask', description: 'Automation.assignTaskDescription' },
};

const CreateTaskModal = ({ open, onClose, onSuccess }: ICreateTaskModalProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showToast } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState<IStep1FormValues | null>(null);
  const [step2Data, setStep2Data] = useState<IStep2FormValues | null>(null);
  const [step3Data, setStep3Data] = useState<IStep3FormValues | null>(null);

  const stepSubmitRef = useRef<(() => void) | null>(null);
  const createMutation = useCreateTaskAutomation();

  const handleNext = () => {
    stepSubmitRef.current?.();
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      onClose();
    }
  };

  const handleStep1Submit = useCallback((data: IStep1FormValues) => {
    setStep1Data(data);
    setCurrentStep(2);
  }, []);

  const handleStep2Submit = useCallback((data: IStep2FormValues) => {
    setStep2Data(data);
    setCurrentStep(3);
  }, []);

  const handleStep3Submit = useCallback((data: IStep3FormValues) => {
    setStep3Data(data);
    setCurrentStep(4);
  }, []);

  const handleStep4Submit = useCallback(
    (data: IStep4FormValues) => {
      if (!step1Data || !step2Data || !step3Data) return;

      // Build inline checklist items — use label based on current language
      const checklistItems: IInlineChecklistItem[] = step2Data.selectedItemsData.map((item) => ({
        label: (isRtl ? item.labelAr : item.labelEn) || item.labelEn || item.labelAr || '',
        isActive: true,
      }));

      const request: ICreateTaskAutomationRequest = {
        name: step1Data.name,
        description: 'Automation Task Description',
        eventType: Number(step1Data.eventTypeId),
        eventTiming: Number(step1Data.eventTimingId),
        timeType: step1Data.timeType ? Number(step1Data.timeType) : null,
        time: step1Data.time ? Number(step1Data.time) : null,
        propertiesIds: step3Data.selectedPropertyIds,
        serviceProviderId: data.assigneeId ?? null,
        categoryId: step1Data.serviceId || '',
        categoryName: step1Data.serviceName || step1Data.serviceId || '',
        checklistItems,
      };

      createMutation.mutate(request, {
        onSuccess: (response) => {
          if (response.isSuccess) {
            handleClose();
            showToast(t('Automation.createTaskSuccess'), { severity: 'success' });
            onSuccess?.();
          } else {
            const msg = response.errorMessages?.join(', ') || t('Automation.createTaskError');
            showToast(msg, { severity: 'error' });
          }
        },
        onError: () => {
          showToast(t('Automation.createTaskError'), { severity: 'error' });
        },
      });
    },
    [step1Data, step2Data, step3Data, createMutation, onSuccess]
  );

  // Reset to step 1 when modal closes/opens
  const handleClose = () => {
    setCurrentStep(1);
    setStep1Data(null);
    setStep2Data(null);
    setStep3Data(null);
    onClose();
  };

  const header = STEP_HEADERS[currentStep] || STEP_HEADERS[1];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      
      PaperProps={{
        className: '!w-[840px] !max-h-[90vh] !min-h-[600px] !rounded-[24px] !shadow-[0px_12px_138.6px_0px_rgba(0,0,0,0.25)] !overflow-hidden !flex !flex-col',
      }}
    >
      {/* ── Fixed Header ── */}
      <div className="shrink-0 pt-6 px-6 flex flex-col">
        <div className={`flex justify-end`}>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            type="button"
          >
            <IoClose size={24} color="#000" />
          </button>
        </div>

        <div className="flex gap-3 py-2.5">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
            const stepIndex = i
            const isActive = stepIndex < currentStep;
            return (
              <div
                key={i}
                className={`flex-1 h-[7px] rounded-[62px] ${
                  isActive ? 'bg-[#5B3FFF]' : 'bg-[rgba(242,242,242,0.98)]'
                }`}
              />
            );
          })}
        </div>

        <div className={'text-start'}>
          <h2 className="!my-0 font-[Tajawal,sans-serif] font-bold text-2xl text-[#261B6A] tracking-[-0.6px] leading-[34px]">
            {t(header.title)}
          </h2>
          <p className="!my-0 font-[Tajawal,sans-serif] font-normal text-base text-[#8E98A1] tracking-[-0.4px] leading-[45px]">
            {t(header.description)}
          </p>
        </div>
      </div>

      {/* ── Scrollable Step Content ── */}
      <div className="px-6 pt-6 pb-4 flex-1 min-h-[200px] overflow-y-auto">
        <div style={{ display: currentStep === 1 ? '' : 'none' }}>
          <StepOne
            open={open}
            isActive={currentStep === 1}
            onSubmit={handleStep1Submit}
            onCancel={handleClose}
            submitRef={stepSubmitRef}
          />
        </div>
        <div style={{ display: currentStep === 2 ? '' : 'none' }}>
          <StepTwo
            serviceId={step1Data?.serviceId ?? ''}
            isActive={currentStep === 2}
            onSubmit={handleStep2Submit}
            submitRef={stepSubmitRef}
          />
        </div>
        <div style={{ display: currentStep === 3 ? '' : 'none' }}>
          <StepThree
            isActive={currentStep === 3}
            onSubmit={handleStep3Submit}
            submitRef={stepSubmitRef}
          />
        </div>
        <div style={{ display: currentStep === 4 ? '' : 'none' }}>
          <StepFour
            isActive={currentStep === 4}
            onSubmit={handleStep4Submit}
            submitRef={stepSubmitRef}
          />
        </div>
      </div>

      {/* ── Fixed Footer ── */}
      <div className="shrink-0 w-full flex items-center justify-between px-6 pt-[25px] pb-6" style={{ borderTop: '1px solid #E8E8EA', borderRadius: '0 0 24px 24px' }}>
          <button
            onClick={handleBack}
            type="button"
            className="cursor-pointer outline-none"
            style={{
              width: 200,
              height: 40,
              backgroundColor: '#FFFFFF',
              borderRadius: 8,
              border: '1px solid #E8E8EA',
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              color: '#666666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {currentStep > 1 ? t('Automation.previous') : t('Automation.cancel')}
          </button>
        <button
          onClick={handleNext}
          type="button"
          disabled={createMutation.isPending}
          className="cursor-pointer outline-none"
          style={{
            width: 200,
            height: 40,
            backgroundColor: createMutation.isPending ? '#9B8FFF' : '#5B3FFF',
            borderRadius: 8,
            border: 'none',
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 700,
            fontSize: 14,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: createMutation.isPending ? 0.7 : 1,
          }}
        >
          {createMutation.isPending
            ? '...'
            : currentStep === TOTAL_STEPS
              ? t('Automation.createTask')
              : t('Automation.next')}
        </button>
      </div>
    </Dialog>
  );
};

export { CreateTaskModal };
export type { IStep1FormValues };
export type { IStep2FormValues };
export type { IStep3FormValues };
export type { IStep4FormValues };
export default CreateTaskModal;
